import React, { useContext, useEffect, useReducer, useState } from "react";
import {
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Grid,
    Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
    DeleteOutline,
    Edit,
    TrendingUp,
    MonetizationOn,
    Input,
} from "@material-ui/icons";
import { SmartToy, AutoAwesome, Output } from "@mui/icons-material";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import AIAgentModal from "../../components/AIAgentModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        backgroundColor: theme.palette.fancyBackground,
    },
    metricCard: {
        padding: theme.spacing(2.5),
        borderRadius: 4,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        background: theme.mode === "light" ? "#f9f9f9" : "#273142",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
    metricIcon: {
        width: 48,
        height: 48,
        borderRadius: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    metricValue: {
        fontWeight: 700,
        fontSize: "1.4rem",
        color: theme.mode === "light" ? "#000" : "#FFF",
    },
    metricLabel: {
        fontSize: "0.78rem",
        color: theme.mode === "light" ? "#000" : "#FFF",
    },
    addBtn: {
        background: "linear-gradient(135deg, #F97316 0%, #FB923C 100%)",
        color: "#fff",
        fontWeight: 600,
        borderRadius: 4,
        textTransform: "none",
        padding: "8px 20px",
        "&:hover": {
            background: "linear-gradient(135deg, #ea6a0a 0%, #f97316 100%)",
        },
    },
    tableRow: {
        "&:hover": { background: theme.palette.action.hover },
    },
    activeChip: {
        background: "#D1FAE5",
        color: "#065F46",
        borderRadius: 4,
        padding: "2px 10px",
        fontSize: "0.75rem",
        fontWeight: 600,
    },
    inactiveChip: {
        background: "#FEE2E2",
        color: "#991B1B",
        borderRadius: 4,
        padding: "2px 10px",
        fontSize: "0.75rem",
        fontWeight: 600,
    },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_AGENTS") {
        // Substitui o estado completamente para evitar entradas fantasma
        return action.payload.filter((a) => a.id);
    }
    if (action.type === "UPDATE_AGENT") {
        const agent = action.payload;
        if (!agent || !agent.id) return state; // ignora agentes sem id válido
        const idx = state.findIndex((a) => a.id === agent.id);
        if (idx !== -1) { state[idx] = agent; return [...state]; }
        return [agent, ...state];
    }
    if (action.type === "DELETE_AGENT") {
        return state.filter((a) => a.id !== action.payload);
    }
    if (action.type === "RESET") return [];
    return state;
};

const AIAgentPage = () => {
    const classes = useStyles();
    const { user, socket } = useContext(AuthContext);
    const [agents, dispatch] = useReducer(reducer, []);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deletingAgent, setDeletingAgent] = useState(null);
    const [metrics, setMetrics] = useState({
        totalTokens: 0,
        promptTokens: 0,
        completionTokens: 0,
        estimatedCost: 0,
    });

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get("/ai-agents"),
            api.get("/ai-agents/metrics"),
        ])
            .then(([agentsRes, metricRes]) => {
                dispatch({ type: "LOAD_AGENTS", payload: agentsRes.data });
                setMetrics(metricRes.data);
            })
            .catch(toastError)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const companyId = user.companyId;
        const onAiAgentEvent = (data) => {
            if (data.action === "create" || data.action === "update") {
                dispatch({ type: "UPDATE_AGENT", payload: data.agent });
            } else if (data.action === "delete") {
                dispatch({ type: "DELETE_AGENT", payload: data.id });
            }
        };
        socket.on(`company-${companyId}-aiagent`, onAiAgentEvent);
        return () => socket.off(`company-${companyId}-aiagent`, onAiAgentEvent);
    }, [socket, user]);

    const handleOpenModal = (agent = null) => {
        setSelectedAgent(agent);
        setModalOpen(true);
    };

    const handleCloseModal = (savedAgent) => {
        setSelectedAgent(null);
        setModalOpen(false);
        if (savedAgent) {
            dispatch({ type: "UPDATE_AGENT", payload: savedAgent });
        }
        // Recarrega métricas após salvar
        api.get("/ai-agents/metrics").then((r) => setMetrics(r.data)).catch(() => { });
    };

    const handleDelete = async (agentId) => {
        if (!agentId || isNaN(Number(agentId))) {
            toast.error(i18n.t("aiAgentPage.deleteErrorInvalid"));
            setDeletingAgent(null);
            // Recarrega a lista para limpar entradas inválidas
            api.get("/ai-agents").then((r) => dispatch({ type: "LOAD_AGENTS", payload: r.data })).catch(() => { });
            return;
        }
        try {
            await api.delete(`/ai-agents/${agentId}`);
            dispatch({ type: "DELETE_AGENT", payload: agentId });
            toast.success(i18n.t("aiAgentPage.deleteSuccess"));
        } catch (err) {
            toastError(err);
        }
        setDeletingAgent(null);
    };

    const metricCards = [
        {
            label: i18n.t("aiAgentPage.metrics.totalTokens"),
            value: metrics.totalTokens.toLocaleString("pt-BR"),
            icon: <TrendingUp style={{ color: "#6366F1" }} />,
            bg: "#EEF2FF",
        },
        {
            label: i18n.t("aiAgentPage.metrics.inputTokens"),
            value: metrics.promptTokens.toLocaleString("pt-BR"),
            icon: <Input style={{ color: "#0EA5E9" }} />,
            bg: "#E0F2FE",
        },
        {
            label: i18n.t("aiAgentPage.metrics.outputTokens"),
            value: metrics.completionTokens.toLocaleString("pt-BR"),
            icon: <Output style={{ color: "#10B981" }} />,
            bg: "#D1FAE5",
        },
        {
            label: i18n.t("aiAgentPage.metrics.estimatedCost"),
            value: `$${metrics.estimatedCost.toFixed(4)}`,
            icon: <MonetizationOn style={{ color: "#F43F5E" }} />,
            bg: "#FFE4E6",
        },
    ];

    return (
        <MainContainer>
            <AIAgentModal
                open={modalOpen}
                onClose={handleCloseModal}
                agentId={selectedAgent?.id}
            />
            <ConfirmationModal
                title={deletingAgent && `${i18n.t("aiAgentPage.deleteConfirmTitle")} "${deletingAgent.name}"?`}
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => handleDelete(deletingAgent?.id)}
            >
                {i18n.t("aiAgentPage.deleteConfirmBody")}
            </ConfirmationModal>

            <MainHeader>
                <Title>
                    <AutoAwesome style={{ marginRight: 8, color: "#F97316", verticalAlign: "middle" }} />
                    {i18n.t("aiAgentPage.title")}
                </Title>
                <MainHeaderButtonsWrapper>
                    <Button
                        className={classes.addBtn}
                        onClick={() => handleOpenModal()}
                        startIcon={<SmartToy />}
                    >
                        {i18n.t("aiAgentPage.addAgent")}
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>

            {/* Métricas */}
            <Grid container spacing={2} style={{ marginBottom: 16, padding: "0 8px" }}>
                {metricCards.map((card) => (
                    <Grid item xs={12} sm={6} md={3} key={card.label}>
                        <div className={classes.metricCard}>
                            <div className={classes.metricIcon} style={{ background: card.bg }}>
                                {card.icon}
                            </div>
                            <div>
                                <Typography className={classes.metricValue}>{card.value}</Typography>
                                <Typography className={classes.metricLabel}>{card.label}</Typography>
                            </div>
                        </div>
                    </Grid>
                ))}
            </Grid>

            <Paper className={classes.mainPaper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{i18n.t("aiAgentPage.table.name")}</TableCell>
                            <TableCell>{i18n.t("aiAgentPage.table.sector")}</TableCell>
                            <TableCell>{i18n.t("aiAgentPage.table.model")}</TableCell>
                            <TableCell align="center">{i18n.t("aiAgentPage.table.maxTokens")}</TableCell>
                            <TableCell align="right">{i18n.t("aiAgentPage.table.tokensUsed")}</TableCell>
                            <TableCell align="center">{i18n.t("aiAgentPage.table.status")}</TableCell>
                            <TableCell align="center">{i18n.t("aiAgentPage.table.actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRowSkeleton columns={7} />
                        ) : (
                            agents.map((agent) => (
                                <TableRow key={agent.id} className={classes.tableRow}>
                                    <TableCell>
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                            {agent.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{agent.queue?.name || "—"}</TableCell>
                                    <TableCell>{agent.model || "gpt-4o-mini"}</TableCell>
                                    <TableCell align="center">{agent.maxTokens || 2000}</TableCell>
                                    <TableCell align="right">
                                        {(agent.totalTokens || 0).toLocaleString("pt-BR")}
                                    </TableCell>
                                    <TableCell align="center">
                                        <span className={agent.isActive ? classes.activeChip : classes.inactiveChip}>
                                            {agent.isActive ? i18n.t("aiAgentPage.status.active") : i18n.t("aiAgentPage.status.inactive")}
                                        </span>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={i18n.t("aiAgentPage.tooltips.edit")}>
                                            <IconButton size="small" onClick={() => handleOpenModal(agent)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={i18n.t("aiAgentPage.tooltips.delete")}>
                                            <IconButton
                                                size="small"
                                                onClick={() => { setDeletingAgent(agent); setConfirmOpen(true); }}
                                            >
                                                <DeleteOutline fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default AIAgentPage;
